// Team Live Review (approved HTML:
// 11-Team-Live-Consistency-Review-v4.html, 2026-10-12 approved stack).
//
// Manager rail. Not reachable for Sales reps in production; the HubSpot
// scope gate (MANAGER_ROLE_REQUIRED) resolves to Blocked for them.
// Default state is illustrative data per Doug's world; tests exercise
// each DataBoundary branch through the state parameter.
//
// W37b (2026-10-20): on a real session the screen reads
// GET /v1/team-live/overview (W37a, sinks/team-live-sink.mjs, READ;
// MEASURED for the url-bootstrap session of user 89608213: `{reps: [10],
// totals: {calls, notes, tasks, repsActiveToday}, presence: {available:
// false}, viewer: {role: "manager"}}`; a sales_rep session gets 403
// MANAGER_ROLE_REQUIRED). It renders the real rep table (HubSpot owners
// with today's engagement counts) and honest "not available" cards for
// presence, queue priorities and exceptions, which have no composed
// source. The design fixture (Jamie, Sarah, Michael and the sample
// amounts) renders only in preview / dev-mock mode.
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { TeamLiveHeader } from './components/TeamLiveHeader.js';
import { TeamLiveMetrics } from './components/TeamLiveMetrics.js';
import { RepAvailability } from './components/RepAvailability.js';
import { QueuePriorities } from './components/QueuePriorities.js';
import { ManagerCoachingCallout } from './components/ManagerCoachingCallout.js';
import { ManagerExceptions } from './components/ManagerExceptions.js';
import { SystemHealth } from './components/SystemHealth.js';
import { NotAvailableCard } from './components/NotAvailableCard.js';
import { RepRollupTable, formatSnapshot, mapRepRollup, type RepRollupRow } from './components/RepRollupTable.js';
import { useManagerRead } from './components/useManagerRead.js';
import {
  TEAM_LIVE_METRICS,
  TEAM_LIVE_REPS,
  QUEUE_PRIORITIES,
  EXCEPTIONS,
  SYSTEM_HEALTH,
  type TeamLiveMetricsSnapshot,
} from './mockData.js';
import './styles.css';

export interface TeamLiveScreenProps {
  stateOverride?: BoundaryState;
}

/** Real overview, fields READ from sinks/team-live-sink.mjs. */
export interface TeamLiveOverview {
  snapshotAt: string | null;
  windowLabel: string | null;
  reps: RepRollupRow[];
  unattributedTotal: number;
  totals: { calls: number; notes: number; tasks: number; touches: number; repsTotal: number; repsActiveToday: number };
  presence: { available: boolean; reason: string | null; note: string | null };
  truncated: boolean;
  viewerRole: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

/** Map the 200 body `{result, overview}` defensively; null when `overview` is absent. */
export function mapTeamLiveOverview(body: unknown): TeamLiveOverview | null {
  const overview = isRecord(body) && isRecord(body.overview) ? body.overview : null;
  if (!overview) return null;
  const totals = isRecord(overview.totals) ? overview.totals : {};
  const presence = isRecord(overview.presence) ? overview.presence : {};
  const unattributed = isRecord(overview.unattributed) ? overview.unattributed : {};
  const win = isRecord(overview.window) ? overview.window : {};
  const viewer = isRecord(overview.viewer) ? overview.viewer : {};
  const reps = mapRepRollup(overview.reps);
  return {
    snapshotAt: readString(overview.snapshotAt),
    windowLabel: readString(win.label),
    reps,
    unattributedTotal: readCount(unattributed.total),
    totals: {
      calls: readCount(totals.calls),
      notes: readCount(totals.notes),
      tasks: readCount(totals.tasks),
      touches: readCount(totals.touches),
      repsTotal: typeof totals.repsTotal === 'number' ? readCount(totals.repsTotal) : reps.length,
      repsActiveToday:
        typeof totals.repsActiveToday === 'number'
          ? readCount(totals.repsActiveToday)
          : reps.filter(r => r.activeToday).length,
    },
    presence: {
      available: presence.available === true,
      reason: readString(presence.reason),
      note: readString(presence.note),
    },
    truncated: overview.truncated === true,
    viewerRole: readString(viewer.role),
  };
}

const NOT_AVAILABLE = 'Not available';

/** Metric tiles from the real totals; every number is read off the body. */
export function liveMetrics(overview: TeamLiveOverview): TeamLiveMetricsSnapshot {
  const t = overview.totals;
  return {
    snapshotDate: formatSnapshot(overview.snapshotAt),
    snapshotTime: overview.windowLabel ?? 'today',
    creditedSalesToday: `${t.touches} touch${t.touches === 1 ? '' : 'es'}`,
    creditedCaption: `${t.calls} calls · ${t.notes} notes · ${t.tasks} tasks · HubSpot, ${overview.windowLabel ?? 'today'}`,
    availableReps: `${t.repsActiveToday} of ${t.repsTotal}`,
    availableCaption: 'Reps with HubSpot activity today · presence not connected',
    openWork: NOT_AVAILABLE,
    openWorkCaption: 'No queue source connected',
    exceptions: NOT_AVAILABLE,
    exceptionsCaption: 'No exceptions source connected',
  };
}

export function TeamLiveScreen({ stateOverride }: TeamLiveScreenProps = {}): ReactNode {
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const read = useManagerRead<unknown>({ path: '/v1/team-live/overview', enabled: !stateOverride });
  const preview = read.status === 'preview';
  const overview = useMemo(
    () => (read.status === 'ready' ? mapTeamLiveOverview(read.body) : null),
    [read],
  );

  // Real-session boundary state from the read. stateOverride wins
  // (tests + state switcher); preview keeps the demo switcher usable.
  const liveState: BoundaryState | null = (() => {
    if (stateOverride || preview) return null;
    switch (read.status) {
      case 'pending':
      case 'loading':
        return { kind: 'loading', label: 'Loading Team Live…' };
      case 'forbidden':
        return {
          kind: 'blocked',
          title: 'Team Live is restricted',
          reason: read.detail ?? `Manager role required for live team metrics; your session role is ${read.role ?? 'unknown'}.`,
          gateName: read.errorCode,
        };
      case 'unauthorized':
        return {
          kind: 'blocked',
          title: 'Session unavailable',
          reason: read.detail ?? 'Open the Sales Workspace from a HubSpot record and retry.',
          gateName: read.errorCode,
        };
      case 'error':
        return {
          kind: 'error',
          title: 'Team Live could not load',
          detail: read.detail ?? `Request failed with status ${read.httpStatus}.`,
          errorCode: read.errorCode,
        };
      case 'ready':
        if (!overview) {
          return { kind: 'empty', title: 'No team snapshot yet', body: 'The overview body carried no snapshot.' };
        }
        return null;
      default:
        return null;
    }
  })();

  const state: BoundaryState = stateOverride
    ? resolveState(kind, stateOverride)
    : liveState ?? (preview ? resolveState(kind, undefined) : { kind: 'default' });

  if (!preview && !stateOverride) {
    return (
      <WorkspaceChrome screen="team-live" rail="manager">
        <div className="demo-navigation" style={{ marginBottom: 14 }}>
          <strong>Team Live</strong>
          <span style={{ marginLeft: 10, color: '#8ba3b8', fontSize: 10 }} data-testid="team-live-mode">
            Live · HubSpot owners and today’s engagements · No live actions
          </span>
        </div>
        <DataBoundary state={state} testId="team-live-boundary">
          {overview ? (
            <>
              <TeamLiveHeader snapshotLabel="LIVE SNAPSHOT" snapshotDate={formatSnapshot(overview.snapshotAt)} />
              <TeamLiveMetrics metrics={liveMetrics(overview)} />
              <div className="n-grid">
                <div className="n-stack">
                  <RepRollupTable
                    reps={overview.reps}
                    title="Team Live"
                    subtitle={`${overview.totals.repsTotal} HubSpot owner${overview.totals.repsTotal === 1 ? '' : 's'} · ${overview.windowLabel ?? 'today'}`}
                    dataComponent="rep-rollup"
                  />
                  {overview.unattributedTotal > 0 ? (
                    <p className="n-body" data-testid="team-live-unattributed">
                      {overview.unattributedTotal} engagement{overview.unattributedTotal === 1 ? '' : 's'} today
                      with no HubSpot owner.
                    </p>
                  ) : null}
                  {overview.truncated ? (
                    <p className="n-body" data-testid="team-live-truncated">
                      Counts are a partial read: HubSpot returned more engagements than one refresh scans.
                    </p>
                  ) : null}
                  <NotAvailableCard
                    title="Queue priorities"
                    reason="NO_QUEUE_SOURCE"
                    note="No sales queue source is composed on this deployment."
                    dataComponent="queue-priorities-unavailable"
                  />
                </div>
                <aside className="n-stack">
                  <ManagerCoachingCallout />
                  <NotAvailableCard
                    title="Needs your attention"
                    reason="NO_EXCEPTIONS_SOURCE"
                    note="No attribution or coverage exception source is composed on this deployment."
                    dataComponent="manager-exceptions-unavailable"
                  />
                  <NotAvailableCard
                    title="Presence and system health"
                    reason={overview.presence.reason ?? 'NO_PRESENCE_SOURCE'}
                    note={overview.presence.note}
                    dataComponent="system-health-unavailable"
                  />
                </aside>
              </div>
            </>
          ) : null}
        </DataBoundary>
      </WorkspaceChrome>
    );
  }

  return (
    <WorkspaceChrome screen="team-live" rail="manager">
      <div className="demo-navigation" style={{ marginBottom: 14 }}>
        <strong>Team Live</strong>
        <span style={{ marginLeft: 10, color: '#8ba3b8', fontSize: 10 }}>
          Design preview · All data illustrative · No live actions
        </span>
      </div>
      <DataBoundary state={state} testId="team-live-boundary">
        <TeamLiveHeader snapshotDate={TEAM_LIVE_METRICS.snapshotDate} />
        <TeamLiveMetrics metrics={TEAM_LIVE_METRICS} />
        <div className="n-grid">
          <div className="n-stack">
            <RepAvailability reps={TEAM_LIVE_REPS} snapshotTime={TEAM_LIVE_METRICS.snapshotTime} />
            <QueuePriorities rows={QUEUE_PRIORITIES} />
          </div>
          <aside className="n-stack">
            <ManagerCoachingCallout />
            <ManagerExceptions rows={EXCEPTIONS} />
            <SystemHealth health={SYSTEM_HEALTH} />
          </aside>
        </div>
      </DataBoundary>
      <StateSwitcher kind={kind} onChange={setKind} disabled={!!stateOverride} />
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Loading Team Live…' };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No reps on shift yet',
        body: 'When reps clock in through My Activity they appear here.',
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Team Live could not load',
        detail: 'The manager presence feed did not respond.',
        errorCode: 'TEAM_LIVE_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'rep-availability', reason: 'presence_feed_stale' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Team Live is restricted',
        reason: 'Manager role required for live team metrics.',
        gateName: 'MANAGER_ROLE_REQUIRED',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Updating coverage proposal…',
        actionName: 'coverage_proposal_preview',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StateSwitcher({
  kind,
  onChange,
  disabled,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
  disabled: boolean;
}): ReactNode {
  if (disabled) return null;
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 24,
        padding: 12,
        border: '1px dashed #c4d4e5',
        borderRadius: 8,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 10, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 11,
            padding: '5px 10px',
            borderRadius: 5,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default TeamLiveScreen;
